/* ========================================================================
 * DOM-based Routing
 * Based on http://goo.gl/EUTi53 by Paul Irish
 *
 * Only fires on body classes that match. If a body class contains a dash,
 * replace the dash with an underscore when adding it to the object below.
 *
 * .noConflict()
 * The routing is enclosed within an anonymous function so that you can
 * always reference jQuery with $, even when in .noConflict() mode.
 * ======================================================================== */

import Swiper from 'swiper';
import { Navigation, A11y } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';

(function ($) {

  /**
   * Initialize Search Toggle
   */
  function initSearchToggle() {
    var toggleMenuItem = document.querySelector('.mega-menu-item-search-toggle');
    var toggleLink = toggleMenuItem ? toggleMenuItem.querySelector('a') : null;
    var mobileSearchOpen = document.querySelector('.mobile-top-bar .search-open');
    var mobileToggleLink = mobileSearchOpen ? mobileSearchOpen.querySelector('.search-toggle-btn') : null;
    var overlay = document.querySelector('.search-overlay');

    if (!overlay) {
      return;
    }

    var searchInput = overlay.querySelector('.search-field');
    var searchSubmit = overlay.querySelector('.search-submit');

    // Prevent keyboard focus on hidden overlay elements
    if (searchInput) {
      searchInput.setAttribute('tabindex', '-1');
    }
    if (searchSubmit) {
      searchSubmit.setAttribute('tabindex', '-1');
    }

    function openSearch() {
      overlay.classList.add('is-open');
      overlay.setAttribute('aria-hidden', 'false');

      // Enable keyboard focus on overlay elements
      if (searchInput) {
        searchInput.removeAttribute('tabindex');
      }
      if (searchSubmit) {
        searchSubmit.removeAttribute('tabindex');
      }

      if (toggleMenuItem) {
        toggleMenuItem.classList.add('search-open');
      }
      if (toggleLink) {
        toggleLink.setAttribute('aria-expanded', 'true');
      }
      if (mobileSearchOpen) {
        mobileSearchOpen.classList.add('is-active');
      }
      if (mobileToggleLink) {
        mobileToggleLink.setAttribute('aria-expanded', 'true');
      }

      if (searchInput) {
        searchInput.focus();
      }
    }

    function closeSearch() {
      overlay.classList.remove('is-open');
      overlay.setAttribute('aria-hidden', 'true');

      // Prevent keyboard focus on hidden overlay elements
      if (searchInput) {
        searchInput.setAttribute('tabindex', '-1');
      }
      if (searchSubmit) {
        searchSubmit.setAttribute('tabindex', '-1');
      }

      if (toggleMenuItem) {
        toggleMenuItem.classList.remove('search-open');
      }
      if (toggleLink) {
        toggleLink.setAttribute('aria-expanded', 'false');
      }
      if (mobileSearchOpen) {
        mobileSearchOpen.classList.remove('is-active');
      }
      if (mobileToggleLink) {
        mobileToggleLink.setAttribute('aria-expanded', 'false');
      }
    }

    function toggleSearch(e) {
      e.preventDefault();
      var isOpen = overlay.classList.contains('is-open');

      if (isOpen) {
        closeSearch();
      } else {
        openSearch();
      }
    }

    // Desktop toggle
    if (toggleLink) {
      toggleLink.addEventListener('click', toggleSearch);
    }

    // Mobile toggle
    if (mobileToggleLink) {
      mobileToggleLink.addEventListener('click', toggleSearch);
    }

    // Close on Escape key
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && overlay.classList.contains('is-open')) {
        closeSearch();
        if (toggleLink) {
          toggleLink.focus();
        }
      }
    });
  }


  /**
   * Initialize Related Content Carousels
   */
  function initRelatedContentCarousels() {
    // News carousel
    var newsCarousel = document.querySelector('.related-content__carousel--news');
    if (newsCarousel) {
      var newsWrapper = newsCarousel.closest('.related-content__carousel-wrapper');
      new Swiper(newsCarousel, {
        modules: [Navigation, A11y],
        slidesPerView: 'auto',
        spaceBetween: 0,
        navigation: {
          nextEl: newsWrapper.querySelector('.related-content__nav--next'),
          prevEl: newsWrapper.querySelector('.related-content__nav--prev'),
        },
        a11y: {
          prevSlideMessage: 'Previous slide',
          nextSlideMessage: 'Next slide',
        },
        on: {
          init: function () {
            newsWrapper.classList.add('at-start');
          },
          slideChange: function (swiper) {
            if (swiper.isBeginning) {
              newsWrapper.classList.add('at-start');
            } else {
              newsWrapper.classList.remove('at-start');
            }
            if (swiper.isEnd) {
              newsWrapper.classList.add('at-end');
            } else {
              newsWrapper.classList.remove('at-end');
            }
          },
        },
      });
    }

    // Events carousel
    var eventsCarousel = document.querySelector('.related-content__carousel--events');
    if (eventsCarousel) {
      var eventsWrapper = eventsCarousel.closest('.related-content__carousel-wrapper');
      new Swiper(eventsCarousel, {
        modules: [Navigation, A11y],
        slidesPerView: 2.5,
        spaceBetween: 0,
        breakpoints: {
          1200: {
            slidesPerView: 2.5,
          },
          992: {
            slidesPerView: 2.2,
          },
          768: {
            slidesPerView: 1.4,
          },
          0: {
            slidesPerView: 1.1,
          },
        },
        navigation: {
          nextEl: eventsWrapper.querySelector('.related-content__nav--next'),
          prevEl: eventsWrapper.querySelector('.related-content__nav--prev'),
        },
        a11y: {
          prevSlideMessage: 'Previous slide',
          nextSlideMessage: 'Next slide',
        },
        on: {
          init: function () {
            eventsWrapper.classList.add('at-start');
          },
          slideChange: function (swiper) {
            if (swiper.isBeginning) {
              eventsWrapper.classList.add('at-start');
            } else {
              eventsWrapper.classList.remove('at-start');
            }
            if (swiper.isEnd) {
              eventsWrapper.classList.add('at-end');
            } else {
              eventsWrapper.classList.remove('at-end');
            }
          },
        },
      });
    }
  }

  // Use this variable to set up the common and page specific functions. If you
  // rename this variable, you will also need to rename the namespace below.
  var Sage = {
    // All pages
    'common': {
      init: function () {
        // JavaScript to be fired on all pages
        initSearchToggle();
        initRelatedContentCarousels();
      },
      finalize: function () {
        // JavaScript to be fired on all pages, after page specific JS is fired
      }
    },
    // Home page
    'home': {
      init: function () {
        // JavaScript to be fired on the home page
      },
      finalize: function () {
        // JavaScript to be fired on the home page, after the init JS
      }
    },
    // About us page, note the change from about-us to about_us.
    'about_us': {
      init: function () {
        // JavaScript to be fired on the about us page
      }
    }
  };

  // The routing fires all common scripts, followed by the page specific scripts.
  // Add additional events for more control over timing e.g. a finalize event
  var UTIL = {
    fire: function (func, funcname, args) {
      var fire;
      var namespace = Sage;
      funcname = (funcname === undefined) ? 'init' : funcname;
      fire = func !== '';
      fire = fire && namespace[func];
      fire = fire && typeof namespace[func][funcname] === 'function';

      if (fire) {
        namespace[func][funcname](args);
      }
    },
    loadEvents: function () {
      // Fire common init JS
      UTIL.fire('common');

      // Fire page-specific init JS, and then finalize JS
      $.each(document.body.className.replace(/-/g, '_').split(/\s+/), function (i, classnm) {
        UTIL.fire(classnm);
        UTIL.fire(classnm, 'finalize');
      });

      // Fire common finalize JS
      UTIL.fire('common', 'finalize');
    }
  };

  // Load Events
  $(document).ready(UTIL.loadEvents);

})(jQuery); // Fully reference jQuery after this point.
